import { supabase } from './supabase'

/**
 * Which database is this? OPS-46 / ADR 0001 — the not-prod banner.
 *
 * ── THE ANSWER COMES FROM THE DATABASE, NEVER FROM THE BUILD ────────────────
 *
 * Not `import.meta.env.MODE`, not a `VITE_ENVIRONMENT` variable, not the
 * hostname. All three describe the BUILD, and a build pointed at the wrong
 * project is exactly the case the banner exists to catch: Netlify's production
 * context served dev for three weeks (OPS-42), and a `VITE_*` flag set to
 * "prod" in that context would have agreed with the mistake. Migration 079
 * stamped each database with its own name for this one read.
 *
 * ── WHAT EACH ANSWER MEANS ON SCREEN ────────────────────────────────────────
 *
 *   'prod'       — nothing. The banner is ABSENT on production, by design.
 *   'dev'        — the strip. The real roster is here (ADR 0001 ruling 2), so
 *                  a person reading it needs to know that what they do reaches
 *                  ~940 real colleagues' data and no real inbox.
 *   'unstamped'  — the strip, louder. 079 returns this rather than null for a
 *                  database nobody has stamped; it should never be served.
 *   UNREACHABLE  — the strip, saying it could not confirm. Rule 12: silence
 *                  here would be a claim that this is prod, and the one time the
 *                  read fails is the one time that claim is worth nothing.
 *
 * `null` from the loader means "not asked yet" and renders nothing — the read
 * is one RPC on shell mount and the strip appears a frame later on dev.
 */
export const ENVIRONMENT_UNREACHABLE = 'unreachable' as const

export type EnvironmentName = 'dev' | 'prod' | 'unstamped' | typeof ENVIRONMENT_UNREACHABLE

export async function loadEnvironmentName(): Promise<EnvironmentName> {
  const { data, error } = await supabase.rpc('environment_name')
  if (error || typeof data !== 'string') return ENVIRONMENT_UNREACHABLE
  if (data === 'dev' || data === 'prod' || data === 'unstamped') return data
  // A stamp value 079's CHECK does not allow cannot come back from a real
  // database; treat anything else as the read having gone wrong.
  return ENVIRONMENT_UNREACHABLE
}

export type EnvironmentBanner = {
  /** Short, uppercase, first on the strip. */
  tag: string
  /** One sentence. Says what this database IS, never what to do about it. */
  body: string
}

/**
 * Pure, so the suite can assert every branch without a database. Returns null
 * exactly when nothing should render: prod, and not-yet-loaded.
 */
export function environmentBanner(name: EnvironmentName | null): EnvironmentBanner | null {
  switch (name) {
    case null:
    case 'prod':
      return null
    case 'dev':
      return {
        tag: 'DEV',
        body: 'This is the development database. Real roster, no real audience — nothing here reaches the company.',
      }
    case 'unstamped':
      return {
        tag: 'UNSTAMPED',
        body: 'This database has not been stamped dev or prod. It should not be serving anyone.',
      }
    case ENVIRONMENT_UNREACHABLE:
      return {
        tag: 'UNCONFIRMED',
        body: 'Could not confirm which database this is.',
      }
  }
}
